import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface FloatingActionsContextType {
  showFloatingActions: boolean;
  setModalOpen: (isOpen: boolean) => void;
}

const FloatingActionsContext = createContext<FloatingActionsContextType | undefined>(undefined);

export const useFloatingActions = () => {
  const context = useContext(FloatingActionsContext);
  if (!context) {
    throw new Error('useFloatingActions must be used within a FloatingActionsProvider');
  }
  return context;
};

interface FloatingActionsProviderProps {
  children: ReactNode;
}

export const FloatingActionsProvider = ({ children }: FloatingActionsProviderProps) => {
  const [showFloatingActions, setShowFloatingActions] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const handleScroll = () => {
      // Show floating actions after scrolling down a full viewport height
      // But hide them if a modal is open
      const viewportHeight = window.innerHeight;
      setShowFloatingActions(!isModalOpen && window.scrollY > viewportHeight);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isModalOpen]);

  const setModalOpen = (isOpen: boolean) => {
    setIsModalOpen(isOpen);
  };

  return (
    <FloatingActionsContext.Provider value={{ showFloatingActions: showFloatingActions && !isModalOpen, setModalOpen }}>
      {children}
    </FloatingActionsContext.Provider>
  );
};